import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Iconify } from '../Minimal/src/components/iconify';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import { toast } from "sonner";
import Header from '../Header';
import Footer from '../Footer';

const AddPurchasesGRN = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('accessToken');
  const roleID = localStorage.getItem("roleID");
  const grnExpiryDateIsMandatory = localStorage.getItem("grnExpiryDateIsMandatory") || "";
  const userId = localStorage.getItem('userId');

  const [newProductList, setNewProductList] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [productData, setProductData] = useState(null);
  const [grnnumber, setGrnnumber] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPurchesItemsList = async () => {
    const formData = new FormData();
    formData.append('id', orderId);

    const apiSalesRouteUrl = `${config.API_URL}/purchases/getpurches_items`;
    try {
      setLoading(true);
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();

      const productData = data.purchase;
      setGrnnumber(data.commonData[0].reference_no);
      setSupplierName(data.commonData[0].name);
      setSupplierId(data.commonData[0].supplierId);
      setProductData(data.ProductData);

      const aggregatedProductData = data.ProductData.reduce((acc, item) => {
        if (!acc[item.product_id]) {
          acc[item.product_id] = {
            expiry_date: item.expiry_date || "",
            piece_stock: item.type == 1 ? parseInt(item.box_stock) : parseInt(item.piece_stock || 0),
          };
        }
        if (item.expiry_date && acc[item.product_id].expiry_date && item.expiry_date < acc[item.product_id].expiry_date) {
          acc[item.product_id].expiry_date = item.expiry_date;
        }
        acc[item.product_id].piece_stock += parseInt(item.piece_stock || 0);

        return acc;
      }, {});

      const aggregatedProductDataArray = Object.keys(aggregatedProductData).map(product_id => ({
        product_id,
        ...aggregatedProductData[product_id],
      }));

      setNewProductList(aggregatedProductDataArray);

      const pdtRows = productData.map((item) => {
        const sizeMatch = item.size ? item.size.match(/^(\d+)x/) : null;
        const sizeValue = sizeMatch ? parseFloat(sizeMatch[1]) : NaN;

        const unitCost = item.split == '0' || item.split == '2'
          ? parseFloat(item.unit_cost) || 0
          : parseFloat(item.piece_cost) || 0;

        const salePrice = item.split == '0' || item.split == '2'
          ? parseFloat(item.price) || 0
          : parseFloat(item.split_price) || 0;

        const productInfo = aggregatedProductDataArray.find(
          (product) => product.product_id === item.product_id
        );

        const expiryData = productInfo
          ? [{ expiryDate: productInfo.expiry_date || "", batchQty: productInfo.piece_stock || 0 }]
          : [{ expiryDate: "", batchQty: 0 }];

        return {
          id: item.id,
          product_id: item.product_id,
          product_code: item.product_code,
          price: item.price || 0,
          piece_cost: item.piece_cost || 0,
          split: item.split || 0,
          type: item.type,
          split_quantity: item.split_quantity || 0,
          split_price: item.split_price || 0,
          name: item.product_name,
          size: item.size || "N/A",
          cost: item.unit_cost || 0,
          rate: item.rate || 0,
          stockCount: item.split == '0' ? parseInt(item.current_stock || 0) : parseInt(item.split_quantity || 0),
          pdtcost: unitCost.toFixed(2),
          salePrice: salePrice.toFixed(2),
          order_qty: item.quantity,
          profit: (salePrice - unitCost).toFixed(2),
          expiryData,
          current_stock: parseInt(item.current_stock || 0),
          category: item.category || item.categorie_name || "",
        };
      });

      setTableRows(pdtRows);
    } catch (error) {
      console.error('API failed while fetching purchase items', error);
      toast.error('Failed to load purchase items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchPurchesItemsList();
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    const formData = new FormData();
    let formIsValid = true;
    let newErrors = {};

    tableRows.forEach((row) => {
      row.expiryData.forEach((data, index) => {
        if (grnExpiryDateIsMandatory === 'Y') {
          if (!data.expiryDate) {
            formIsValid = false;
            if (!newErrors[row.id]) {
              newErrors[row.id] = { batchQty: "", expiryDate: "" };
            }
            newErrors[row.id].expiryDate = "Expiry date is required.";
          }
        }
        const isMeatsCategory = row && (row.category === "MEATS" || row.category === "Meats" || row.category === "meats");
        const batchQtyValue = data.batchQty === "" ? 0 : (isMeatsCategory ? parseFloat(data.batchQty) : parseInt(data.batchQty)) || 0;
        if (!data.batchQty || data.batchQty === "" || batchQtyValue === 0) {
          formIsValid = false;
          if (!newErrors[row.id]) {
            newErrors[row.id] = { batchQty: "", expiryDate: "" };
          }
          newErrors[row.id].batchQty = "Batch quantity is required and cannot be 0.";
        }
      });
    });

    setErrors(newErrors);

    if (!formIsValid) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    formData.append("purchase_id", orderId.trim());
    formData.append("user_id", userId);
    formData.append("invoiceno", invoiceNo);

    if (productData && productData.length > 0) {
      formData.append("is_grn", "Y");
    } else {
      formData.append("is_grn", "N");
    }

    formData.append("supplierId", supplierId);
    formData.append("po_id", orderId);

    tableRows.forEach((row) => {
      formData.append("type[]", row.type === '0' || row.type === '1' ? 'Box' : 'Piece');
      formData.append("cost[]", row.pdtcost);
      formData.append("sale_price[]", row.salePrice);
      formData.append("profit[]", row.profit);
      formData.append("order_qty[]", row.order_qty);
      formData.append("code[]", row.product_code);
      formData.append("name[]", row.name);
      formData.append("product_id[]", row.product_id);
      formData.append("id[]", row.id);
      formData.append("margin[]", ((row.salePrice - row.pdtcost) / row.pdtcost * 100).toFixed(2));

      row.expiryData.forEach((expiry) => {
        if (row.product_id) {
          const qtyKey = `qty_${row.product_id}[]`.trim();
          const edateKey = `edate_${row.product_id}[]`.trim();

          formData.append(qtyKey, expiry.batchQty);
          formData.append(edateKey, expiry.expiryDate);
        }
      });
    });

    const apiPurchaseRouteUrl = `${config.API_URL}/purchases/grnadd`;

    try {
      const response = await fetch(apiPurchaseRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const result = await response.json();
      const msg = result.message;

      if (result.success == true) {
        toast.success("GRN added successfully.");
        setTimeout(() => {
          navigate('/purchases/purchases_order');
        }, 1500);
      } else {
        toast.error(msg || "Failed to add GRN");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error posting purchase data:", error);
      toast.error("An error occurred while submitting");
      setIsSubmitting(false);
    }
  };

  const handleCancel = (id) => {
    setTableRows(prevRows => prevRows.filter(row => row.id !== id));
    toast.success('Product removed from list');
  };

  const handleCostChange = (id, newCost) => {
    if (newCost === "" || newCost === ".") {
      setTableRows(prevRows => {
        return prevRows.map(row => {
          if (row.id === id) {
            return { ...row, pdtcost: newCost };
          }
          return row;
        });
      });
      return;
    }
    
    if (!/^\d*\.?\d*$/.test(newCost)) {
      return;
    }
    
    const parts = newCost.split('.');
    if (parts.length > 1 && parts[1].length > 2) {
      return;
    }
    
    const numValue = parseFloat(newCost);
    if (isNaN(numValue)) {
      return;
    }
    
    if (numValue > 9999.99) {
      toast.error("Limit: Maximum cost price is 9999.99");
      return;
    }
    
    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.id === id) {
          const salePrice = parseFloat(row.salePrice) || 0.00;
          const profit = (salePrice - numValue).toFixed(2);
          return {
            ...row,
            pdtcost: newCost,
            profit
          };
        }
        return row;
      });
    });
  };

  const handleOptionChange = (id, pdtType) => {
    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.id === id) {
          const pdtcost = pdtType == 'Box' ? row.price : row.piece_cost;
          const salePrice = pdtType == 'Box' ? parseFloat(row.price || 0.00).toFixed(2) : parseFloat(row.split_price || 0.00).toFixed(2);
          const type = pdtType === 'Box' ? 'box' : 'piece';
          const profit = (salePrice - pdtcost).toFixed(2);
          const stockCount = pdtType == 'Box' ? parseInt(row.current_stock || 0) : parseInt(row.split_quantity || 0);

          return {
            ...row,
            type,
            pdtcost,
            profit,
            stockCount,
            salePrice
          };
        }
        return row;
      });
    });
  };

  const handleSelePriceChange = (id, newCost) => {
    if (newCost === "" || newCost === ".") {
      setTableRows(prevRows => {
        return prevRows.map(row => {
          if (row.id === id) {
            return { ...row, salePrice: newCost };
          }
          return row;
        });
      });
      return;
    }
    
    if (!/^\d*\.?\d*$/.test(newCost)) {
      return;
    }
    
    const parts = newCost.split('.');
    if (parts.length > 1 && parts[1].length > 2) {
      return;
    }
    
    const numValue = parseFloat(newCost);
    if (isNaN(numValue)) {
      return;
    }
    
    if (numValue > 9999.99) {
      toast.error("Limit: Maximum sale price is 9999.99");
      return;
    }
    
    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.id === id) {
          const pdtcost = parseFloat(row.pdtcost) || 0;
          const profit = (numValue - pdtcost).toFixed(2);
          return {
            ...row,
            salePrice: newCost,
            pdtcost,
            profit
          };
        }
        return row;
      });
    });
  };

  const handleExpiryDataChange = (e, itemId, index, field) => {
    let newValue = e.target.value;

    if (field === 'batchQty') {
      const row = tableRows.find((item) => item.id === itemId);
      const isMeatsCategory = row && (row.category === "MEATS" || row.category === "Meats" || row.category === "meats");
      
      let isValid = false;
      if (newValue === "") {
        isValid = true;
      } else if (isMeatsCategory) {
        isValid = /^\d*\.?\d*$/.test(newValue);
      } else {
        isValid = /^[0-9]*$/.test(newValue);
      }

      if (isValid) {
        const numValue = newValue === "" ? 0 : (isMeatsCategory ? parseFloat(newValue) : parseInt(newValue)) || 0;
        if (numValue > 9999) {
          toast.error("Limit: Maximum order quantity is 9999");
          return;
        }
        setTableRows(prevItems => prevItems.map(item =>
          item.id === itemId
            ? {
              ...item,
              expiryData: item.expiryData.map((data, i) =>
                i === index ? { ...data, [field]: newValue } : data
              )
            }
            : item
        ));
      }
    } else if (field === 'expiryDate') {
      setTableRows(prevItems => prevItems.map(item =>
        item.id === itemId
          ? {
            ...item,
            expiryData: item.expiryData.map((data, i) =>
              i === index ? { ...data, [field]: newValue } : data
            )
          }
          : item
      ));
    }
  };

  const addExpiryData = (itemId) => {
    setTableRows(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
            ...item,
            expiryData: [...item.expiryData, { expiryDate: "", batchQty: "" }]
          }
          : item
      )
    );
  };

  const removeExpiryData = (itemId, index) => {
    setTableRows(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              expiryData: item.expiryData.filter((_, i) => i !== index)
          }
          : item
      )
    );
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'F3') return;
      e.preventDefault();
      setTableRows((prev) =>
        prev.map((row) => ({
          ...row,
          expiryData: row.expiryData.map((d) => ({ ...d,  batchQty: String(row.order_qty) })),
        }))
      );
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{p: 1.5 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#212b36" }}>
                  Add GRN
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }} display="flex" gap={1} alignItems="center">
                <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  GRN No :
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {grnnumber}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }} display="flex" gap={1} alignItems="center">
                <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  Supplier Name :
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {supplierName}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  label="Supplier Invoice No"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  
                />
              </Grid2>
            </Grid2>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(145, 158, 171, 0.16)', overflow: 'hidden'}}>
              <TableContainer className="table-cell-border">
                <Table size="small" className="table-cell-border" sx={{ tableLayout: 'auto' }}>
                  <TableHead className="table-cell-header">
                    <TableRow>
                      <TableCell sx={{ width: 180 }}>Code - Name - Size</TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>Type</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>Cost Price</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>Sale Price</TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>Profit</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>Current Stock</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>Order Qty</TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>Margin</TableCell>
                      <TableCell sx={{ width: 350 }}>Qty - Expiry Date</TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No product added</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableRows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: '0.75rem', width: 200 }}>
                            {row.product_code} - {row.name} - {row.size}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>
                            {row.split == 2 ? (
                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                  style={{ width: '75px', height: '28px'}}
                                  value={row.type == 1 ? 'Box' : 'Piece'}
                                  onChange={(e) => handleOptionChange(row.id, e.target.value)}
                                  sx={{
                                    "& .MuiSelect-select": {
                                      fontSize: '0.8rem',
                                    }
                                  }}
                                >
                                  <MenuItem value="Box">Box</MenuItem>
                                  <MenuItem value="Piece">Piece</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography sx={{ fontSize: '0.75rem' }}>
                                {row.split == 0 ? 'Box' : 'Piece'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', width: 80 }}>
                            <TextField
                              type="text"
                              className="input-border"
                              value={row.pdtcost}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                  handleCostChange(row.id, value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              size="small"
                              inputProps={{
                                style: { width: '50px', textAlign: 'right' },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "28px",
                                }
                              }}
                              disabled={roleID === '20'}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>
                            <TextField
                              className="input-border"
                              type="text"
                              value={row.salePrice}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                  handleSelePriceChange(row.id, value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              size="small"
                              inputProps={{ style: { textAlign: 'right', width: '50px' } }}
                              disabled={roleID === '20'}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "28px",
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', width: 80 }}>
                            {row.profit}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', width: 100 }}>
                            {row.stockCount}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', width: 100 }}>
                            {row.order_qty}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', width: 80 }}>
                            {parseFloat(row.pdtcost) ? ((parseFloat(row.salePrice) - parseFloat(row.pdtcost)) / parseFloat(row.pdtcost) * 100).toFixed(2) : '0.00'} %
                          </TableCell>
                          <TableCell sx={{ width: 300 }}>
                            <Stack spacing={1}>
                              {row.expiryData.map((data, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField
                                    type="text"
                                    className="input-border"
                                    value={data.order_qty || data.batchQty || ''}
                                    onChange={(e) => handleExpiryDataChange(e, row.id, index, 'batchQty')}
                                    onKeyDown={(e) => {
                                      const isMeatsCategory = row && (row.category === "MEATS" || row.category === "Meats" || row.category === "meats");
                                      if (!isMeatsCategory && ['e', 'E', '+', '-', '.'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    size="small"
                                    placeholder="Qty"
                                    inputProps={{ style: { textAlign: 'center' } }}
                                    sx={{
                                      width: '70px',
                                      "& .MuiOutlinedInput-root": {
                                        height: "28px",
                                      }
                                    }}
                                    error={!!errors[row.id]?.batchQty}
                                  />
                                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                      className="input-border"
                                      format="dd-MM-yyyy"
                                      value={data.expiryDate && data.expiryDate !== '' ? new Date(data.expiryDate) : null}
                                      minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                      shouldDisableDate={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const selectedDate = new Date(date);
                                        selectedDate.setHours(0, 0, 0, 0);
                                        return selectedDate < today;
                                      }}
                                      onChange={(newValue) => {
                                        if (!newValue) {
                                          handleExpiryDataChange({ target: { value: '' } }, row.id, index, 'expiryDate');
                                          return;
                                        }
                                        
                                        if (isNaN(newValue.getTime())) {
                                          toast.error("Invalid date selected");
                                          return;
                                        }
                                        
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const selectedDate = new Date(newValue);
                                        selectedDate.setHours(0, 0, 0, 0);
                                        
                                        if (selectedDate < today) {
                                          toast.error("Cannot select a previous date");
                                          return;
                                        }
                                        
                                        const year = selectedDate.getFullYear();
                                        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                        const day = String(selectedDate.getDate()).padStart(2, '0');
                                        const dateString = `${year}-${month}-${day}`;
                                        
                                        handleExpiryDataChange({ target: { value: dateString } }, row.id, index, 'expiryDate');
                                      }}
                                      slotProps={{
                                        textField: {
                                          size: 'small',
                                          error: !!errors[row.id]?.expiryDate,
                                          sx: { width: '160px',
                                            "& .MuiOutlinedInput-root": {
                                              height: "32px",
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {index === 0 ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => addExpiryData(row.id)}
                                      color="primary"
                                    >
                                      <Iconify icon="eva:plus-outline" width={20} />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      onClick={() => removeExpiryData(row.id, index)}
                                      color="error"
                                    >
                                      <Iconify icon="eva:trash-2-outline" width={20} />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                              {errors[row.id]?.batchQty && (
                                <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                                  {errors[row.id].batchQty}
                                </Typography>
                              )}
                              {errors[row.id]?.expiryDate && (
                                <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                                  {errors[row.id].expiryDate}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleCancel(row.id)}
                              color="error"
                            >
                              <Iconify icon="eva:trash-2-outline" width={20} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Stack direction="row" spacing={2} p={2} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              size="medium"
              disabled={isSubmitting || loading}
              className="button-variant-contained"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default AddPurchasesGRN;
